/**
* Responsibility:
* - Resolve numeric GF form id from form element metadata.
*
* Touches:
* - Reads `data-formid` and `id` attributes on form node.
*
* Guarantees:
* - Returns positive form id or `0` when unavailable.
*/
export function getFormIdFromElement(form) {
	if (!(form instanceof HTMLElement)) {
		return 0;
	}

	const dataFormId = Number.parseInt(String(form.dataset.formid || ''), 10);
	if (Number.isFinite(dataFormId) && dataFormId > 0) {
		return dataFormId;
	}

	const idMatch = String(form.id || '').match(/^gform_(\d+)$/);
	if (!idMatch) {
		return 0;
	}

	const parsed = Number.parseInt(idMatch[1], 10);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
* Responsibility:
* - Read source page number from GF submission hidden input.
*
* Touches:
* - Reads `gform_source_page_number_{formId}` input in current form.
*
* Guarantees:
* - Returns positive page number or `0` when unavailable.
*/
export function readSubmissionSourcePage(form, formId) {
	if (!(form instanceof HTMLElement)) {
		return 0;
	}

	const sourceInput = form.querySelector(`[name="gform_source_page_number_${formId}"]`);
	if (!(sourceInput instanceof HTMLInputElement || sourceInput instanceof HTMLTextAreaElement || sourceInput instanceof HTMLSelectElement)) {
		return 0;
	}

	const parsed = Number.parseInt(String(sourceInput.value || ''), 10);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
* Responsibility:
* - Resolve current page number from shared app state populated by GF events.
*
* Touches:
* - Reads `app.currentPage`.
*
* Guarantees:
* - Returns positive integer page or `0` when unknown.
*/
export function readCurrentPageNumber(app) {
	const pageNumber = Number(app?.currentPage || 0);
	if (!Number.isFinite(pageNumber) || pageNumber <= 0) {
		return 0;
	}

	return Math.trunc(pageNumber);
}

/**
* Responsibility:
* - Detect `previous` submission lifecycle actions in GF pre-submission payload.
*
* Touches:
* - Reads `data.submissionType` and GF submission constants.
*
* Guarantees:
* - Returns `true` only for previous-navigation submissions.
*/
export function isPreviousSubmissionType(data) {
	const submissionType = String(data?.submissionType || '').trim().toLowerCase();
	const previousConstant = String(window?.gform?.submission?.SUBMISSION_TYPE_PREVIOUS || 'previous').trim().toLowerCase();

	return submissionType !== '' && (submissionType === previousConstant || submissionType === 'previous');
}

/**
* Responsibility:
* - Detect `next` submission lifecycle actions in GF pre-submission payload.
*
* Touches:
* - Reads `data.submissionType` and GF submission constants.
*
* Guarantees:
* - Returns `true` only for next-navigation submissions.
*/
export function isNextSubmissionType(data) {
	const submissionType = String(data?.submissionType || '').trim().toLowerCase();
	const nextConstant = String(window?.gform?.submission?.SUBMISSION_TYPE_NEXT || 'next').trim().toLowerCase();

	return submissionType !== '' && (submissionType === nextConstant || submissionType === 'next');
}

/**
* Responsibility:
* - Detect `submit` submission lifecycle actions in GF pre-submission payload.
*
* Touches:
* - Reads `data.submissionType` and GF submission constants.
*
* Guarantees:
* - Returns `true` only for final-submit submissions.
*/
export function isSubmitSubmissionType(data) {
	const submissionType = String(data?.submissionType || '').trim().toLowerCase();
	const submitConstant = String(window?.gform?.submission?.SUBMISSION_TYPE_SUBMIT || 'submit').trim().toLowerCase();

	return submissionType !== '' && (submissionType === submitConstant || submissionType === 'submit');
}
